import type { BeforeRequestHook } from 'ky';
import { HTTP_HEADERS } from '../../../../auth/const';
import { extractInterfaceFromRoute } from '../../../extractInterfaceFromRoute';
import isNative from '../../../native/isNative';
import { requestWithNewUrl } from '../../../request';
import { getPublicApiURL, replaceLastApiPath } from '../../../url/publicUrl';
import type { QueryMeta } from '../../types';
import { isServer } from '../../utils';
import type { HookGetterArgs } from '../types';
import { addDpopHeader } from './dpop';
import { getInternalSvcRequest } from './internalSvc';

const AUTH_PREFIX = '/auth/';

/**
 * Converts the given URL to a public access page URL (/auth/...)
 * LoggedInPage -> apis start with /api/...
 * PublicPage -> apis start with /auth/...
 * @param urlObj - The URL to convert in string
 */
export const getPublicPageURL = (urlObj: URL): URL => {
  urlObj.pathname = replaceLastApiPath(urlObj.pathname, AUTH_PREFIX);
  return urlObj;
};

export function publicPageURL(url: string): string {
  const urlObj = new URL(url);
  return getPublicPageURL(urlObj).toString();
}

const makePublicPageRequest = async (request: Request): Promise<Request> => {
  const newUrl = publicPageURL(request.url);

  const newRequest = await requestWithNewUrl(request, newUrl);
  // remove after ky issue is fixed https://github.com/sindresorhus/ky/issues/387
  // returning from a hook will block the next hooks. so we need to call the function and return the value
  return (await getInternalSvcRequest(newRequest)) || newRequest;
};

/**
 * Converts the given URL to a public API URL
 * @param url - The URL to convert in string
 * @param queryMeta - Optional metadata for the query
 */
export function publicApiURL(url: string, queryMeta?: QueryMeta): string {
  const urlObj = new URL(url);
  return getPublicApiURL(urlObj, queryMeta).toString();
}

const makePublicRequest = async (request: Request, queryMeta?: QueryMeta): Promise<Request> => {
  const newUrl = publicApiURL(request.url, queryMeta);

  const newRequest = await requestWithNewUrl(request, newUrl);
  // remove after ky issue is fixed https://github.com/sindresorhus/ky/issues/387
  // returning from a hook will block the next hooks. so we need to call the function and return the value
  return (await getInternalSvcRequest(newRequest)) || newRequest;
};

const setInterfaceId = (
  request: Request,
  interfaceId?: string | null,
  disableInterfaceHeader?: boolean,
) => {
  // caller opted out of app scoping (e.g. share modal user/team lookup must return
  // all platform users, not just the current package's) — skip the header.
  if (disableInterfaceHeader) return;
  if (interfaceId) {
    request.headers.set(HTTP_HEADERS.AppId, interfaceId);
  }
};

export const getInterfaceHeaders = ({
  fetchArgs: { url },
  options,
}: HookGetterArgs): BeforeRequestHook => {
  const interfaceHeadersHook: BeforeRequestHook = async (request) => {
    if (isServer() && url.startsWith('/')) {
      const { headers: nextHeaders } = await import('next/headers');
      const header = await nextHeaders();

      const interfaceId = extractInterfaceFromRoute(header.get('pathname') ?? '');
      setInterfaceId(request, interfaceId, options.meta?.disableInterfaceHeader);

      if (header.get(HTTP_HEADERS.PUBLIC) || options.meta?.isPublicInterface) {
        return makePublicRequest(request, options.meta);
      }

      // route /auth/* only for anonymous visitors. logged-in users on a public
      // page keep /api/* so their session cookie is honored.
      if (options.meta?.isPublicPage && !options.meta?.isUserLoggedIn) {
        return makePublicPageRequest(request);
      }
    }

    if (!isServer()) {
      /**
       * Determine the interface ID based on the environment
       * If running in native environment, use the value from options.meta.interfaceId
       * Otherwise, extract the interface ID from the current browser route
       */
      const interfaceId = isNative()
        ? options.meta?.interfaceId
        : extractInterfaceFromRoute(window.location.pathname);
      setInterfaceId(request, interfaceId, options.meta?.disableInterfaceHeader);

      // returning a Request skips the DPoP hook (same ky issue linked above), so sign here
      if (options.meta?.isPublicInterface) {
        return addDpopHeader(await makePublicRequest(request, options.meta), options);
      }

      if (options.meta?.isPublicPage && !options.meta?.isUserLoggedIn) {
        return addDpopHeader(await makePublicPageRequest(request), options);
      }
    }
  };
  return interfaceHeadersHook;
};
